/** Severity helpers for the AI Skin Analysis Report redesign. */

export function sortConcernsBySeverity(items = []) {
  return [...(items || [])].sort((a, b) => {
    const sa = Number(a?.score);
    const sb = Number(b?.score);
    const na = Number.isFinite(sa) ? sa : 100;
    const nb = Number.isFinite(sb) ? sb : 100;
    if (na !== nb) return na - nb; // lower health score = more severe first
    return String(a?.label || a?.class_name || '').localeCompare(String(b?.label || b?.class_name || ''));
  });
}

export function getConcernStatusChip(score) {
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  if (n <= 59) return { label: 'Focus Area', tone: 'focus' };
  if (n <= 74) return { label: 'Monitor', tone: 'monitor' };
  return { label: 'Good', tone: 'good' };
}

export function getConcernStatusTone(score) {
  return getConcernStatusChip(score).tone;
}

export function getConcernSeverityTag(score) {
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  if (n <= 39) return { label: 'High', tone: 'high' };
  if (n <= 74) return { label: 'Medium', tone: 'medium' };
  return { label: 'Low', tone: 'low' };
}

/** Reference-style severity wording under the concern title (Detailed Skin Analysis). */
export function getDetailedSeverityLabel(score, concernLabel = '') {
  const n = Math.max(0, Math.min(100, Number(score) || 0));
  const name = String(concernLabel || '').toLowerCase();
  if (/texture|smooth|uneven/.test(name) && n <= 74 && n > 59) {
    return { label: 'Slightly Uneven', tone: 'monitor' };
  }
  if (n <= 49) return { label: 'Moderate', tone: 'focus' };
  if (n <= 59) return { label: 'Visible', tone: 'focus' };
  if (n <= 74) return { label: 'Mild', tone: 'monitor' };
  return { label: 'Low', tone: 'good' };
}

export function buildDetailedConcernBlurb(item, description = '') {
  const raw = String(description || '').trim();
  if (raw) {
    const first = raw.split(/[.!?]/)[0]?.trim();
    return first ? `${first}.` : raw;
  }
  const label = String(item?.label || item?.class_name || 'This area').trim();
  const score = Number(item?.score);
  const n = Number.isFinite(score) ? Math.max(0, Math.min(100, score)) : 70;
  if (n <= 59) return `${label} shows visible imbalance and needs focused care.`;
  if (n <= 74) return `${label} is mild and worth monitoring in your routine.`;
  return `${label} looks stable with low visible concern.`;
}

export function formatSkinColorLabel(raw) {
  const v = String(raw || '').trim().toLowerCase().replace(/\s+/g, '_');
  if (!v || v === 'unknown') return null;
  const map = {
    fair: 'Fair',
    light: 'Light',
    medium: 'Medium',
    olive: 'Olive',
    brown: 'Brown',
    deep: 'Deep',
  };
  return map[v] || (v.charAt(0).toUpperCase() + v.slice(1).replace(/_/g, ' '));
}

/** Rough improvement potential from weakest concerns (higher = more room to improve). */
export function computeImprovementPotential(items = []) {
  const scores = (items || [])
    .map((i) => Number(i?.score))
    .filter((n) => Number.isFinite(n));
  if (!scores.length) return null;
  const weakest = [...scores].sort((a, b) => a - b).slice(0, Math.min(5, scores.length));
  const avgWeak = weakest.reduce((s, n) => s + n, 0) / weakest.length;
  return Math.max(0, Math.min(100, Math.round(100 - avgWeak)));
}

export function buildFallbackTips(items = [], skinType = null) {
  const tips = [];
  const labels = (items || []).slice(0, 5).map((i) => String(i?.label || i?.class_name || '').toLowerCase());
  if (labels.some((l) => l.includes('hydrat') || l.includes('dull'))) {
    tips.push('Drink water regularly and use a hydrating moisturizer.');
  }
  if (labels.some((l) => l.includes('pigment') || l.includes('spot') || l.includes('uneven'))) {
    tips.push('Wear broad-spectrum sunscreen each morning.');
  }
  if (labels.some((l) => l.includes('pore') || l.includes('acne') || l.includes('oil'))) {
    tips.push('Keep a gentle cleanse and avoid over-exfoliating.');
  }
  if (labels.some((l) => l.includes('wrinkle') || l.includes('line') || l.includes('dark circle'))) {
    tips.push('Prioritize 7–8 hours of sleep for recovery.');
  }
  if (labels.some((l) => l.includes('smooth') || l.includes('texture'))) {
    tips.push('Exfoliate gently a few times a week for smoother texture.');
  }
  if (labels.some((l) => l.includes('oxygen'))) {
    tips.push('Keep circulation healthy with movement and consistent cleansing.');
  }
  if (skinType && /dry/i.test(skinType)) {
    tips.push('Seal in moisture after cleansing while skin is damp.');
  }
  if (skinType && /oil|combination/i.test(skinType)) {
    tips.push('Use lightweight, non-comedogenic textures in humid weather.');
  }
  tips.push('Patch-test new products before full-face use.');
  return tips.slice(0, 6);
}

/** Strip time-specific wording from tips (today, tonight, this week, etc.). */
export function sanitizeTipText(tip) {
  let out = String(tip || '').trim();
  if (!out) return '';
  out = out.replace(
    /\b(today|tonight|tomorrow|yesterday|this\s+(?:morning|afternoon|evening|week|weekend|month)|right\s+now|currently|lately|nowadays|for\s+now|at\s+the\s+moment)\b/gi,
    '',
  );
  out = out.replace(/\s+,/g, ',').replace(/\s{2,}/g, ' ').replace(/\s+([.!?])/g, '$1').trim();
  out = out.replace(/^[,;:\-\s]+|[,;:\-\s]+$/g, '');
  if (out && out[0] === out[0].toLowerCase()) {
    out = out[0].toUpperCase() + out.slice(1);
  }
  return out;
}
